import React, { useMemo } from 'react';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppSelector } from 'flow/hooks/useRedux';
import { selectExtensionsStatuses } from 'flow/selectors/ExtensionSelectors';
import { selectQueueMembers } from 'flow/selectors/queue-selectors';
import { QueueKey } from 'flow/types/EntityKeys';
import SupervisorBarAgent from '../SupervisorBarAgent';
import AgentsLoggedInInfo from './AgentsLoggedInInfo';

interface Props {
    queueKey: QueueKey;
}

const inCall = (status: string) =>
    status === 'inCall' || status === 'inExternalCall';

const SectionAgentsLoggedIn = ({ queueKey }: Props) => {
    const { i18next } = useAppContext();

    const queueMembers = useAppSelector((state) =>
        selectQueueMembers(state, queueKey),
    );
    const extensionsStatuses = useAppSelector(selectExtensionsStatuses);

    const allQueueMembersLoggedIn = useMemo(
        () => queueMembers?.filter((member) => member.get('loggedIn')),
        [queueMembers],
    );

    const queueMembersTrimmedLoggedIn = useMemo(
        () => allQueueMembersLoggedIn?.slice(0, 100),
        [allQueueMembersLoggedIn],
    );

    const queueMembersSorted = useMemo(() => {
        return queueMembersTrimmedLoggedIn?.sort((memberA, memberB) => {
            const memberAInCall = inCall(
                extensionsStatuses?.get(memberA.get('extensionKey')),
            );
            const memberBInCall = inCall(
                extensionsStatuses?.get(memberB.get('extensionKey')),
            );

            if (memberAInCall && !memberBInCall) {
                return -1;
            } else if (!memberAInCall && memberBInCall) {
                return 1;
            } else {
                return 0;
            }
        });
    }, [queueMembersTrimmedLoggedIn, extensionsStatuses]);

    return (
        <div className="flex h-4 w-full cursor-default flex-row gap-1.5">
            <AgentsLoggedInInfo
                queueKey={queueKey}
                number={allQueueMembersLoggedIn?.size}
            />
            <div className="flex items-center gap-x-px overflow-x-auto">
                {queueMembersSorted && !queueMembersSorted.isEmpty() ? (
                    queueMembersSorted?.map((item) => (
                        <SupervisorBarAgent
                            agentKey={item.get('extensionKey')}
                            key={`sba-${item.get('extensionKey')}`}
                        />
                    ))
                ) : (
                    <span className="text-xs text-gray-400 dark:text-gray-800">
                        {i18next.t('Supervisor.QueueRow.NoOneLoggedIn')}
                    </span>
                )}
            </div>
        </div>
    );
};

export default SectionAgentsLoggedIn;
